import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useSEO, jsonLdBreadcrumb, jsonLdFAQ } from "@/lib/seo";

const HIW_FAQS = [
  { q: "How do I buy a vehicle-backed note on FinanceLocal?", a: "Browse the marketplace, filter by yield, grade, state, or vehicle attributes, open a listing to review payment history and supporting documents, submit an offer, and pay the escrow deposit via Stripe when your offer is accepted. Funds are held until assignment paperwork is signed by both parties." },
  { q: "How do I sell a vehicle note on FinanceLocal?", a: "From your dashboard open Sell Notes → New Listing, enter the vehicle and loan details, upload supporting documents (retail installment contract, title, payment history, insurance, inspection), and submit for admin moderation. Approved listings are published to the public marketplace." },
  { q: "How does dealer origination work?", a: "Approved dealers browse pre-approval marketplace applications, submit pre-approval terms (max amount, APR ≤28%, term, payment frequency), and pay a $2,500 refundable Stripe escrow. Once the borrower selects a dealer and they agree on a vehicle, the dealer has 2 business days to fund the loan." },
  { q: "What is manual credit review?", a: "FinanceLocal underwriters review each application and its uploaded documents by hand. No automated consumer-reporting-agency credit pull is performed. See the ECOA disclosure on the Legal page for details." },
  { q: "What are the note grades A through D?", a: "Grade A represents excellent payment history; B is good payment history; C is moderate risk; D is high risk. Grades reflect the borrower's demonstrated history combined with collateral condition." },
  { q: "What note categories exist?", a: "Notes fall into five categories: Performing (current), Semi-Performing (occasional late payments), Non-Performing (delinquent), Repo Ready (significant delinquency with repossession likely), and Fresh Origination (recently originated with limited history)." },
];

export default function HowItWorks() {
  useSEO({
    title: "How FinanceLocal Works — Buy, Sell, and Fund Vehicle Notes",
    description: "Step-by-step guide to buying, selling, and originating vehicle-backed notes on FinanceLocal. Learn how the peer-to-peer marketplace, $2,500 escrow, and manual credit review work.",
    canonical: "https://myfinancelocal.com/how-it-works",
    keywords: "how to buy vehicle notes, how to sell auto notes, dealer financing marketplace, note origination process, seller financing notes explained",
    jsonLd: [
      jsonLdBreadcrumb([
        { name: "Home", url: "https://myfinancelocal.com/" },
        { name: "How It Works", url: "https://myfinancelocal.com/how-it-works" },
      ]),
      jsonLdFAQ(HIW_FAQS),
    ],
  });
  return (
    <div data-testid="page-how-it-works" className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16">
      <div className="font-overline text-accent">Platform Walkthrough</div>
      <h1 className="font-display text-5xl mt-2">How FinanceLocal Works</h1>
      <p className="text-muted-foreground mt-3 max-w-3xl">
        FinanceLocal is a peer-to-peer marketplace for vehicle-backed notes. There is one unified <b>User</b> account
        that lets you buy notes, sell notes, originate new loans through the pre-approval marketplace, or apply for
        vehicle financing. Approved <b>Dealers</b> get the same capabilities plus the ability to source vehicles for
        funded originations.
      </p>

      <section className="mt-14 space-y-12">
        <Workflow
          n={1}
          title="Buy Existing Vehicle Notes"
          who="User · Dealer"
          steps={[
            "Browse the marketplace and filter by yield, grade, state, vehicle attributes, or category.",
            "Open a note to review payment history, balance, term, supporting documents, and seller history.",
            "Submit an offer at or below the asking price.",
            "The seller may accept, counter, or reject. Accepted offers automatically close all other pending offers on that note.",
            "On acceptance, pay the escrow deposit via Stripe. Funds are held until the assignment paperwork is signed by both sides; then escrow is released and the note is assigned to you.",
          ]}
          cta={{ to: "/notes", label: "Browse the Marketplace" }}
        />
        <Workflow
          n={2}
          title="Sell Existing Vehicle Notes"
          who="User · Dealer (approved)"
          steps={[
            "From your dashboard, open the Sell Notes tab and click New Listing.",
            "Enter vehicle, original loan amount, remaining balance, APR, payment frequency, term, payment history, and asking price.",
            "Upload supporting documents — retail installment contract, title, payment history, insurance, inspection.",
            "Submit for admin moderation. Approved listings appear in the public marketplace as Active.",
            "Review incoming offers in your dashboard. Accept, counter, or reject. You may edit the listing or withdraw it any time before an offer is accepted.",
          ]}
          cta={{ to: "/dashboard", label: "Open My Dashboard" }}
        />
        <Workflow
          n={3}
          title="Originate New Vehicle Loans (Pre-Approval Marketplace)"
          who="User · Dealer"
          steps={[
            "Open a published loan application and review the anonymized borrower profile (credit tier, income range, vehicle preferences).",
            "Submit a pre-approval commitment: maximum amount, maximum APR (≤28%), term, payment frequency.",
            "Pay the fixed $2,500 escrow deposit via Stripe. Your pre-approval becomes Active and visible to the borrower.",
            "When the borrower selects you and you agree on a specific vehicle, the funding deadline starts: 2 business days.",
            "Fund within the deadline and the loan is originated — the resulting Note is automatically assigned to you and your $2,500 deposit is released.",
            "If you miss the deadline, the deposit is forfeited: 40% Dealer · 40% FinanceLocal · 20% Applicant.",
          ]}
          cta={{ to: "/applications", label: "Browse Applications" }}
        />
        <Workflow
          n={4}
          title="Apply for Vehicle Financing"
          who="User"
          steps={[
            "Click Apply for Loan from your dashboard.",
            "Fill in identity, employment, residence, and vehicle-preference details. Upload supporting documents (driver's license, proof of income, proof of insurance, proof of residence).",
            "Submit your application. You can edit or withdraw it at any time before manual credit review begins.",
            "A FinanceLocal administrator performs manual credit review — no automated credit-bureau pull is used.",
            "Once published, investors and dealers offer pre-approval terms. Pick the best offer and agree on a specific vehicle.",
            "Once the loan funds, the dealer hands you the keys; your monthly payments go to the assigned note-holder.",
          ]}
          cta={{ to: "/apply", label: "Apply for Loan" }}
        />
      </section>

      <section className="mt-20 border-t border-border pt-12 grid md:grid-cols-2 gap-12">
        <div>
          <h2 className="font-display text-3xl">Grade Definitions</h2>
          <div className="mt-6 border border-border divide-y divide-border">
            {GRADES.map((g) => (
              <div key={g.grade} className="flex items-center justify-between p-4">
                <div className="font-mono-data font-semibold">Grade {g.grade}</div>
                <div className="text-sm text-muted-foreground">{g.def}</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="font-display text-3xl">Category Definitions</h2>
          <div className="mt-6 border border-border divide-y divide-border">
            {STATUSES.map((s) => (
              <div key={s.k} className="p-4">
                <div className="font-overline text-primary">{s.k}</div>
                <div className="text-sm text-muted-foreground mt-1">{s.def}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-16 border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-6 text-sm" data-testid="how-it-works-manual-review-note">
        <div className="font-overline text-amber-800 dark:text-amber-300">Important Note</div>
        <p className="mt-2 text-amber-900 dark:text-amber-100">
          <b>Underwriting is performed manually.</b> FinanceLocal does <b>not</b> currently perform an
          automated credit-bureau pull. A platform administrator reviews each application using the
          information and documents you upload. See the ECOA Disclosure on our <Link to="/legal?t=ecoa" className="underline">Legal page</Link>.
        </p>
      </section>
    </div>
  );
}

function Workflow({ n, title, who, steps, cta }) {
  return (
    <div className="grid md:grid-cols-[140px_1fr] gap-6 items-start" data-testid={`workflow-${n}`}>
      <div>
        <div className="font-mono-data text-6xl text-primary font-light leading-none">0{n}</div>
        <div className="font-overline text-muted-foreground mt-2 text-xs">{who}</div>
      </div>
      <div className="border-l-2 border-border pl-8">
        <h2 className="font-display text-3xl">{title}</h2>
        <ol className="mt-5 space-y-2 list-decimal pl-5 text-sm text-muted-foreground leading-relaxed">
          {steps.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
        {cta && (
          <Button asChild className="rounded-none uppercase text-xs tracking-wider mt-6" data-testid={`workflow-cta-${n}`}>
            <Link to={cta.to}>{cta.label} <ArrowRight className="w-4 h-4 ml-1" /></Link>
          </Button>
        )}
      </div>
    </div>
  );
}

const GRADES = [
  { grade: "A", def: "Excellent payment history" },
  { grade: "B", def: "Good payment history" },
  { grade: "C", def: "Moderate risk" },
  { grade: "D", def: "High risk" },
];
const STATUSES = [
  { k: "Performing", def: "Current and paying as agreed." },
  { k: "Semi-Performing", def: "Occasional late payments but currently paying." },
  { k: "Non-Performing", def: "Delinquent and collection efforts required." },
  { k: "Repo Ready", def: "Significant delinquency with repossession likely." },
  { k: "Fresh Origination", def: "Recently originated notes with limited payment history." },
];
